/// <reference types="vite/client" />

// src/prototype/services/strategicExecutionPlanService.ts
// GET /api/v2/strategic-execution-plan/:clientId?portfolio=id1,id2,...

const API_BASE =
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_BACKEND_URL ||
  'http://localhost:8080';

// ── Shared shapes ────────────────────────────────────────────────────────────

export interface SEPRoiRange {
  low: number;
  high: number;
}

export interface SEPSelectedInitiative {
  use_case_id: number;
  name: string;
  category_name: string;
  category_description?: string;
  ease_of_implementation_score?: number;
  rank: number;
  annual_benefit_conservative?: number;
  annual_benefit_base?: number;
  annual_benefit_aggressive?: number;
  payback_months?: number;
  roi_estimate: SEPRoiRange;
  effort_tier: string;
  implementation_effort?: string;
  time_to_value?: string;
  value_score?: number;
  value_band?: string;
  validation_score?: number;
  complexity?: string;
  duration?: string;
  execution_posture?: string;
  quick_win?: boolean;
}

export interface SEPExecutionPillar {
  id: string;
  icon: string;
  title: string;
  description: string;
}

export interface SEPPortfolioBucketInitiative {
  use_case_id: number;
  name: string;
  category_name: string;
  annual_benefit_base?: number;
  roiEstimate?: SEPRoiRange;
  timeToValue?: string;
  timeline?: string;
  execution_posture?: string;
  rank?: number;
}

export interface SEPPortfolioBucket {
  key: string;
  label: string;
  subtitle?: string;
  count: number;
  initiatives: SEPPortfolioBucketInitiative[];
}

export interface SEPInitiativePortfolioItem {
  index: number;
  use_case_id: number;
  rank: number;
  name: string;
  category_name: string;
  complexity?: string;
  implementationEffort?: string;
  effort_tier?: string;
  timeToValue?: string;
  timeline?: string;
  roiEstimate: SEPRoiRange;
  validationScore?: number;
  value_band?: string;
  executionRationale?: string;
  scenario_projections?: Record<string, { roiHigh: number; payback_months?: number }>;
  scenarios?: Record<string, { roiHigh: number; payback_months?: number }>;
}

export interface SEPRoadmapInitiative {
  use_case_id: number;
  name: string;
  implementation_effort?: string;
  start_month: number;
  end_month: number;
  phases?: { name: string; start_month: number; end_month: number }[];
  milestones?: { label: string; months_from_start: number; value_note?: string; depends_on?: string | null }[];
}

export interface SEPResourceInitiative {
  index?: number;
  use_case_id: number;
  name: string;
  effort_tier?: string;
  fte_total: number;
  roles: { role: string; team: string; fte: number; scope: string }[];
}

export interface SEPRiskItem {
  risk_id?: string;
  name: string;
  category?: string;
  likelihood: string;
  impact: string;
  mitigation: string;
  owner?: string;
}

export interface SEPSharedDependency {
  id: string;
  title: string;
  priority?: string;
  related_categories?: string[];
  related_initiatives?: string[];
}

export interface SEPApprovalStep {
  step: number;
  title: string;
  description: string;
  responsible_group?: string;
  is_final?: boolean;
}

export interface SEPOversightCadence {
  frequency: string;
  meeting_type: string;
  participants: string;
}

export interface SEPRecommendedNextStep {
  step: number;
  title: string;
  description: string;
  timeline: string;
  pilot_initiatives?: { use_case_id: number; name: string }[];
}

export interface StrategicExecutionPlanSections {
  executive_summary?: {
    organization_name: string;
    portfolio_roi: {
      annual_benefit_base?: number;
      roi_low_millions: number;
      roi_high_millions: number;
    };
    initiatives_selected_count: number;
    execution_horizon: string;
    quick_win_count: number;
    selected_initiatives: SEPSelectedInitiative[];
  };
  execution_approach?: {
    effort_tier?: { quick_win: number; complex: number; moderate: number };
    category_name?: string[];
    pillars: SEPExecutionPillar[];
  };
  portfolio_overview?: {
    organization_name?: string;
    buckets: SEPPortfolioBucket[];
    bucket_legend?: { key: string; label: string; description: string }[];
  };
  initiative_portfolio?: {
    organization_name?: string;
    initiatives_count?: number;
    initiatives: SEPInitiativePortfolioItem[];
  };
  execution_roadmap?: {
    organization_name?: string;
    horizon_months: number;
    initiatives: SEPRoadmapInitiative[];
    sequencing_insights?: {
      quick_win_count: number;
      moderate_count: number;
      complex_count: number;
      horizon_months: number;
    };
  };
  resource_requirements?: {
    organization_name?: string;
    initiatives_count?: number;
    initiatives: SEPResourceInitiative[];
    capacity_planning_notes?: string[];
  };
  financial_summary?: {
    portfolio_value_range: SEPRoiRange & { low_millions?: number; high_millions?: number };
    initiatives_selected_count?: number;
    roi_comparison: {
      use_case_id?: number;
      name: string;
      roi_estimate: SEPRoiRange;
      bar_width_percent?: number;
    }[];
    scenarios: Record<string, { roiHigh: number }>;
    waterfall_table: {
      use_case_id?: number;
      name: string;
      conservative: number;
      base: number;
      aggressive: number;
      payback_months?: number;
      payback_months_conservative?: number | null;
      payback_months_base?: number | null;
      payback_months_aggressive?: number | null;
    }[];
    total_portfolio?: { conservative: number; base: number; aggressive: number };
  };
  governance_framework?: {
    organization_name?: string;
    approval_workflow: SEPApprovalStep[];
    oversight_cadence: SEPOversightCadence[];
  };
  risks_and_dependencies?: {
    organization_name?: string;
    shared_portfolio_dependencies: SEPSharedDependency[];
    risk_register: {
      by_initiative: {
        use_case_id: number;
        initiative_name: string;
        category_name?: string;
        risks: SEPRiskItem[];
      }[];
      all_risks?: SEPRiskItem[];
    };
  };
  approval_checklist?: {
    organization_name?: string;
    portfolio_roi: {
      annual_benefit_base?: number;
      roi_low?: number;
      roi_high?: number;
      roi_low_millions?: number;
      roi_high_millions?: number;
      display_range?: string;
    };
    execution_horizon: string;
    initiatives_selected_count: number;
    remaining_programs_count?: number;
    quick_win_initiative?: { use_case_id: number; name: string } | null;
    advisory_summary: string[];
    recommended_next_steps: SEPRecommendedNextStep[];
  };
}

export interface StrategicExecutionPlanResponse {
  success: boolean;
  client_id: string;
  generated_at?: string;
  meta?: {
    finalist_count?: number;
    finalist_ids?: number[];
    portfolio_filter_provided?: boolean;
  };
  sections: StrategicExecutionPlanSections;
}

export class StrategicExecutionPlanError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'StrategicExecutionPlanError';
    this.status = status;
  }
}

/**
 * GET /api/v2/strategic-execution-plan/:clientId?portfolio=id1,id2,...
 */
export async function fetchStrategicExecutionPlan(
  clientId: string,
  portfolioIds: (string | number)[]
): Promise<StrategicExecutionPlanResponse> {
  if (!clientId) {
    throw new StrategicExecutionPlanError('Client ID is required.');
  }

  const portfolio = portfolioIds
    .map(id => String(id).trim())
    .filter(Boolean)
    .join(',');

  const params = portfolio ? `?portfolio=${encodeURIComponent(portfolio)}` : '';
  const url = `${API_BASE}/api/v2/strategic-execution-plan/${encodeURIComponent(clientId)}${params}`;

  let res: Response;
  try {
    res = await fetch(url, { credentials: 'include' });
  } catch {
    throw new StrategicExecutionPlanError(
      'Unable to reach the Strategic Execution Plan service. Check your network connection and ensure the backend is running.'
    );
  }

  if (!res.ok) {
    let detail = '';
    try {
      const errBody = await res.json();
      detail = errBody?.message || errBody?.error || '';
    } catch {
      /* ignore parse errors */
    }
    throw new StrategicExecutionPlanError(
      detail || `Strategic Execution Plan API returned ${res.status}.`,
      res.status
    );
  }

  let body: StrategicExecutionPlanResponse;
  try {
    body = await res.json();
  } catch {
    throw new StrategicExecutionPlanError('Invalid response from Strategic Execution Plan API.');
  }

  if (!body?.success) {
    throw new StrategicExecutionPlanError('Strategic Execution Plan API reported failure.');
  }

  if (!body.sections || typeof body.sections !== 'object') {
    throw new StrategicExecutionPlanError('Strategic Execution Plan response is missing section data.');
  }

  return body;
}
